// The screen look: System, Light, Dark or Night (Neomaris v2 design system).
// System follows the browser's light or dark setting. Night turns every screen
// red on black for the helm at night, and is only ever chosen by the owner.
//
// The choice is saved in this browser, in localStorage under STORAGE_KEY.
// index.html applies it before first paint with the same key, so there is no
// flash. A browser with no saved look opens in Dark (TOM-944). A signed-in
// account also keeps its last System, Light or Dark on the server, and
// lib/accountScreenLook.ts brings it to a new browser; Night stays per device.
import { useEffect, useState } from 'react';

export type ScreenLook = 'system' | 'light' | 'dark' | 'night';

export const SCREEN_LOOKS: readonly { value: ScreenLook; label: string }[] = [
  { value: 'system', label: 'System' },
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
  { value: 'night', label: 'Night' },
];

export const STORAGE_KEY = 'tombolo.screenLook';
const EVENT = 'tombolo:screenlook';

/** The look of a browser that has never chosen one: the website, Try it, sign-in. */
export const DEFAULT_LOOK: ScreenLook = 'dark';

export function isLook(value: unknown): value is ScreenLook {
  return value === 'system' || value === 'light' || value === 'dark' || value === 'night';
}

/** The look this browser saved, or null when it never chose one. */
export function readStoredLook(): ScreenLook | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return isLook(stored) ? stored : null;
  } catch {
    return null;
  }
}

export function readScreenLook(): ScreenLook {
  return readStoredLook() ?? DEFAULT_LOOK;
}

/** Sets `data-theme` on <html>. System removes it, so the CSS media query decides. */
export function applyScreenLook(look: ScreenLook): void {
  const root = document.documentElement;
  if (look === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', look);
}

export function setScreenLook(look: ScreenLook): void {
  try {
    localStorage.setItem(STORAGE_KEY, look);
  } catch {
    // Private windows can refuse storage; the look still applies to this page.
  }
  applyScreenLook(look);
  window.dispatchEvent(new CustomEvent(EVENT, { detail: look }));
}

/** Calls `onChange` with every look this tab sets. Returns the unsubscribe. */
export function onScreenLookChange(onChange: (look: ScreenLook) => void): () => void {
  const listener = (e: Event) => {
    const detail = (e as CustomEvent<unknown>).detail;
    if (isLook(detail)) onChange(detail);
  };
  window.addEventListener(EVENT, listener);
  return () => window.removeEventListener(EVENT, listener);
}

/** The light or dark look actually showing, with System resolved. */
export function resolvedLook(look: ScreenLook): Exclude<ScreenLook, 'system'> {
  if (look !== 'system') return look;
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function useScreenLook(): [ScreenLook, (look: ScreenLook) => void] {
  const [look, setLook] = useState<ScreenLook>(() => readScreenLook());
  useEffect(() => {
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<ScreenLook>).detail;
      if (isLook(detail)) setLook(detail);
    };
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return;
      const next = isLook(e.newValue) ? e.newValue : DEFAULT_LOOK;
      applyScreenLook(next);
      setLook(next);
    };
    window.addEventListener(EVENT, onChange);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(EVENT, onChange);
      window.removeEventListener('storage', onStorage);
    };
  }, []);
  return [look, setScreenLook];
}
